import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Users, Briefcase, MessageSquare, TrendingUp, Plus, CalendarCheck, Clock, CheckCircle, XCircle, Handshake, UserCircle, Building2, Mail, FileText } from 'lucide-react';
import { toast} from 'sonner';
import ProfileCompletionBanner from '../components/ProfileCompletionBanner';
import TrialBanner from '../components/TrialBanner';
import SubscriptionTierWidget from '../components/SubscriptionTierWidget';
import OnboardingWizard from '../components/OnboardingWizard';
import { useFreelanceRestrictions } from '../hooks/useFreelanceStatus';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, company } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [reservationsSummary, setReservationsSummary] = useState(null);
  const [projectUsage, setProjectUsage] = useState(null);
  const [myProjects, setMyProjects] = useState([]);
  const [trialStatus, setTrialStatus] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [myResources, setMyResources] = useState([]);
  
  // Get freelance status
  const { isRepresented, operatingPartnerId } = useFreelanceRestrictions();
  const isFreelance = user?.is_freelance === true;

  // Check if this is user's first visit (show onboarding)
  useEffect(() => {
    if (!user) return;
    
    // Use user-specific key for localStorage
    const onboardingKey = `hasSeenOnboarding_${user.id}`;
    const hasSeenOnboarding = localStorage.getItem(onboardingKey);
    
    // Check if user was created within last 7 days (more flexible)
    const isNewUser = user?.created_at && 
      new Date(user.created_at).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000;
    
    // Show onboarding if: never seen AND (new user OR first time logging in)
    if (!hasSeenOnboarding && isNewUser) {
      // Delay slightly to let dashboard load first
      setTimeout(() => setShowOnboarding(true), 500);
    }
  }, [user]);

  const handleCloseOnboarding = () => {
    setShowOnboarding(false);
    if (user?.id) {
      localStorage.setItem(`hasSeenOnboarding_${user.id}`, 'true');
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // Fetch resources for represented freelancers
    if (isRepresented || isFreelance) {
      fetchMyResources();
    }
  }, [isRepresented, isFreelance]);
  
  const fetchMyResources = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/resources/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyResources(response.data);
    } catch (error) {
      console.error('Failed to load resources:', error);
    }
  };

  const fetchDashboardData = async () => {
    const token = localStorage.getItem('token');
    const isPM = user?.role === 'project_manager';
    
    try {
      const requests = [
        axios.get(`${API}/stats/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/pre-reservations/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/auth/trial-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ];

      // Add PM-specific requests
      if (isPM) {
        requests.push(
          axios.get(`${API}/projects/activation/usage`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/projects/my`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        );
      }

      const responses = await Promise.all(requests);
      setStats(responses[0].data);
      setReservationsSummary(responses[1].data);
      
      // Handle trial status (might fail for old users without trial fields)
      try {
        setTrialStatus(responses[2].data);
      } catch (err) {
        console.warn('Trial status not available:', err);
        setTrialStatus(null);
      }
      
      // Set user profile for displaying name
      if (responses[3]?.data?.user_profile) {
        setUserProfile(responses[3].data.user_profile);
      }
      
      if (isPM) {
        setProjectUsage(responses[4].data);
        
        // Filter projects to only show those active in the current month
        const allProjects = responses[5].data;
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        
        const currentMonthProjects = allProjects.filter(project => {
          if (!project.is_activated) return false;
          
          const startDate = new Date(project.start_date);
          const endDate = project.end_date ? new Date(project.end_date) : null;
          
          // Check if project is active during current month
          const startsBeforeOrDuringMonth = startDate <= new Date(currentYear, currentMonth + 1, 0);
          const endsAfterOrDuringMonth = !endDate || endDate >= new Date(currentYear, currentMonth, 1);
          
          return startsBeforeOrDuringMonth && endsAfterOrDuringMonth;
        });
        
        setMyProjects(currentMonthProjects);
      }
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const toggleProjectActivation = async (projectId, currentStatus) => {
    const token = localStorage.getItem('token');
    const endpoint = currentStatus ? 'deactivate' : 'activate';
    
    try {
      await axios.post(`${API}/projects/${projectId}/${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Project ${currentStatus ? 'deactivated' : 'activated'} successfully`);
      fetchDashboardData(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update project status');
    }
  };

  const isRM = user?.role === 'resource_manager' || user?.role === 'operating_partner';

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Pending' },
      accepted: { bg: 'bg-green-100', text: 'text-green-800', label: 'Accepted' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="dashboard">
      {/* Onboarding Wizard for new users */}
      <OnboardingWizard 
        isOpen={showOnboarding} 
        onClose={handleCloseOnboarding}
        user={user}
      />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 fade-in">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B3240' }}>
            {(() => {
              // Get user name parts from userProfile
              const firstName = userProfile?.first_name?.trim() || '';
              const lastName = userProfile?.last_name?.trim() || '';
              
              // Build full name - ALWAYS prioritize user name over company
              let displayName = '';
              if (firstName && lastName) {
                displayName = `${firstName} ${lastName}`;
              } else if (firstName) {
                displayName = firstName;
              } else if (lastName) {
                displayName = lastName;
              } else {
                // Only use company name as last resort
                displayName = company?.name || 'Partner';
              }
              
              // Check if user was created in the last hour (first login)
              const isFirstLogin = user?.created_at && 
                new Date(user.created_at).getTime() > Date.now() - 3600000;
              
              return isFirstLogin 
                ? `Welcome, ${displayName}!`
                : `Welcome back, ${displayName}!`;
            })()}
          </h1>
          <p className="text-lg text-gray-600">
            {isRepresented 
              ? 'Your consultant profile is managed by SCS Schnittker Consulting. Stay visible and get matched to the best ServiceNow projects.' 
              : (isRM 
                  ? 'Manage your ServiceNow resources and find new opportunities' 
                  : 'Find the right ServiceNow consultants for your projects'
                )
            }
          </p>
        </div>

        {/* Profile Completion Banner */}
        <ProfileCompletionBanner user={user} />

        {/* Trial Banner */}
        <TrialBanner trialStatus={trialStatus} userRole={user?.role} pilotStatus={user?.pilot_status} />

        {/* Top Section: Stats + Quick Actions */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="spinner"></div>
          </div>
        ) : (
          <>
            {/* REPRESENTED FREELANCER DASHBOARD */}
            {isRepresented ? (
              <>
                {/* Profile Summary Card */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 slide-in">
                  {/* My Profile Status */}
                  <Card className="card-hover border-l-4 border-purple-400 bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Profile Status</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <div>
                          {myResources.filter(r => r.is_activated).length > 0 ? (
                            <span className="text-lg font-bold text-green-600 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5" />
                              Active
                            </span>
                          ) : (
                            <span className="text-lg font-bold text-yellow-600 flex items-center gap-2">
                              <Clock className="w-5 h-5" />
                              Inactive
                            </span>
                          )}
                        </div>
                        <UserCircle className="w-8 h-8 text-purple-500" />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        {myResources.filter(r => r.is_activated).length > 0 
                          ? 'Your profile is visible to clients'
                          : 'Activate your profile to be visible'
                        }
                      </p>
                    </CardContent>
                  </Card>
                  
                  {/* Active Collaborations */}
                  <Card className="card-hover border-l-4 border-blue-400 bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Active Collaborations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-3xl font-bold text-blue-600">
                          {stats?.active_collaborations || 0}
                        </span>
                        <Handshake className="w-8 h-8 text-blue-500" />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Ongoing projects with clients</p>
                    </CardContent>
                  </Card>
                  
                  {/* Completed Projects */}
                  <Card className="card-hover border-l-4 border-green-400 bg-white">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-sm font-medium text-gray-600">Completed Projects</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-between">
                        <span className="text-3xl font-bold text-green-600">
                          {stats?.completed_projects || 0}
                        </span>
                        <CheckCircle className="w-8 h-8 text-green-500" />
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Successfully completed missions</p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Quick Actions for Represented Freelancer */}
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <Card className="bg-white slide-in">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base" style={{ color: '#1B3240' }}>Quick Actions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col gap-3">
                        <Button
                          className="h-14 flex items-center justify-start gap-3 btn-snow"
                          onClick={() => navigate('/resources')}
                          data-testid="quick-view-profile-btn"
                        >
                          <UserCircle className="w-5 h-5" />
                          <span>View My Profile</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                          onClick={() => navigate('/collaborations')}
                          data-testid="quick-collaborations-btn"
                        >
                          <Handshake className="w-5 h-5" />
                          <span>My Collaborations</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                          onClick={() => navigate('/messages')}
                          data-testid="quick-messages-btn"
                        >
                          <MessageSquare className="w-5 h-5" />
                          <span>Messages</span>
                        </Button>
                        <Button
                          variant="outline"
                          className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                          onClick={() => navigate('/reservations')}
                          data-testid="quick-reservations-btn"
                        >
                          <CalendarCheck className="w-5 h-5" />
                          <span>Reservations</span>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* How it Works Card */}
                  <Card className="bg-gradient-to-br from-purple-50 to-blue-50 border-purple-200 slide-in">
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2" style={{ color: '#1B3240' }}>
                        <Building2 className="w-5 h-5 text-purple-600" />
                        You are represented by SCS
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-gray-600 mb-4">
                        As a represented freelancer, your Operating Partner handles commercial negotiations and project matching for you.
                      </p>
                      <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-purple-700">1</span>
                          </div>
                          <p className="text-sm text-gray-700">Keep your profile up to date with your latest skills and availability</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-purple-700">2</span>
                          </div>
                          <p className="text-sm text-gray-700">SCS matches you with ServiceNow projects from their client network</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-purple-700">3</span>
                          </div>
                          <p className="text-sm text-gray-700">Receive reservation requests and start collaborations</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        className="mt-4 w-full border-purple-300 text-purple-700 hover:bg-purple-50"
                        onClick={() => navigate('/getting-started')}
                      >
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                </div>
              </>
            ) : (
            <>
            {/* Two Column Layout - Standard Dashboard */}
            <div className="flex flex-col lg:flex-row gap-6 mb-8">
              {/* Stats Cards - Left Side (70%) */}
              <div className="flex-1 lg:w-[70%]">
                <TooltipProvider>
                  <div className={`grid ${isRM ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-2'} gap-6 slide-in`}>
                    {isRM ? (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-emerald-500 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Total Resources</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold" style={{ color: '#1B3240' }} data-testid="total-resources">
                                    {stats?.total_resources || 0}
                                  </span>
                                  <Users className="w-8 h-8 text-emerald-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Total number of resources in your company</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Total number of resources in your company</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-teal-500 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Available Resources</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold text-emerald-600" data-testid="available-resources">
                                    {stats?.available_resources || 0}
                                  </span>
                                  <Users className="w-8 h-8 text-teal-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Resources currently available for new assignments</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Resources currently available for new assignments</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-blue-400 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Active Collaborations</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold text-blue-600" data-testid="active-collaborations">
                                    {stats?.active_collaborations || 0}
                                  </span>
                                  <Handshake className="w-8 h-8 text-blue-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Ongoing and pending collaborations</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Collaborations in progress or awaiting contract</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-emerald-500 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Completed Projects</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold text-emerald-600" data-testid="completed-projects">
                                    {stats?.completed_projects || 0}
                                  </span>
                                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Successfully completed collaborations</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Total number of completed collaborations</p>
                          </TooltipContent>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-emerald-500 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Total Projects</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold" style={{ color: '#1B3240' }} data-testid="total-projects">
                                    {stats?.total_projects || 0}
                                  </span>
                                  <Briefcase className="w-8 h-8 text-emerald-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">All projects created by your company</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>All projects created by your company</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-teal-500 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Active Projects This Month</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold text-emerald-600" data-testid="active-projects">
                                    {projectUsage?.current_active || 0}
                                  </span>
                                  <TrendingUp className="w-8 h-8 text-teal-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Projects active for staffing this month (used for billing)</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Projects active for staffing this month (used for billing)</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-blue-400 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Active Collaborations</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold text-blue-600" data-testid="active-collaborations">
                                    {stats?.active_collaborations || 0}
                                  </span>
                                  <Handshake className="w-8 h-8 text-blue-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Ongoing and pending collaborations</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Collaborations in progress or awaiting contract</p>
                          </TooltipContent>
                        </Tooltip>

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Card className="card-hover border-l-4 border-emerald-500 bg-white">
                              <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-medium text-gray-600">Completed Projects</CardTitle>
                              </CardHeader>
                              <CardContent>
                                <div className="flex items-center justify-between">
                                  <span className="text-3xl font-bold text-emerald-600" data-testid="completed-projects">
                                    {stats?.completed_projects || 0}
                                  </span>
                                  <CheckCircle className="w-8 h-8 text-emerald-500" />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">Successfully completed collaborations</p>
                              </CardContent>
                            </Card>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Total number of completed collaborations</p>
                          </TooltipContent>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </TooltipProvider>
              </div>

              {/* Quick Actions - Right Side (30%) */}
              <div className="lg:w-[30%]">
                <Card className="slide-in bg-white h-full">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base" style={{ color: '#1B3240' }}>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-col gap-3">
                      {isRM ? (
                        <>
                          <Button
                            className="h-14 flex items-center justify-start gap-3 btn-snow"
                            onClick={() => navigate('/resources?action=create')}
                            data-testid="quick-add-resource-btn"
                          >
                            <Plus className="w-5 h-5" />
                            <span>Add Resource</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                            onClick={() => navigate('/matching')}
                            data-testid="quick-find-projects-btn"
                          >
                            <Briefcase className="w-5 h-5" />
                            <span>Find Projects</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                            onClick={() => navigate('/reservations')}
                            data-testid="quick-reservations-btn"
                          >
                            <CalendarCheck className="w-5 h-5" />
                            <span>Reservations</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                            onClick={() => navigate('/messages')}
                            data-testid="quick-messages-btn"
                          >
                            <MessageSquare className="w-5 h-5" />
                            <span>Messages</span>
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            className="h-14 flex items-center justify-start gap-3 btn-snow"
                            onClick={() => navigate('/projects?action=create')}
                            data-testid="quick-add-project-btn"
                          >
                            <Plus className="w-5 h-5" />
                            <span>Add Project</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                            onClick={() => navigate('/matching')}
                            data-testid="quick-find-resources-btn"
                          >
                            <Users className="w-5 h-5" />
                            <span>Find Resources</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                            onClick={() => navigate('/reservations')}
                            data-testid="quick-reservations-btn"
                          >
                            <CalendarCheck className="w-5 h-5" />
                            <span>Reservations</span>
                          </Button>
                          <Button
                            variant="outline"
                            className="h-14 flex items-center justify-start gap-3 border-2 border-gray-300 hover:border-gray-400"
                            onClick={() => navigate('/messages')}
                            data-testid="quick-messages-btn"
                          >
                            <MessageSquare className="w-5 h-5" />
                            <span>Messages</span>
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Subscription Tier Widget */}
            {trialStatus && (
              <div className="mb-8 slide-in">
                <SubscriptionTierWidget 
                  trialStatus={trialStatus} 
                  userRole={user?.role}
                  pilotStatus={user?.pilot_status}
                  usage={{
                    activated_count: !isRM ? (projectUsage?.current_active || 0) : (stats?.activated_resources || 0)
                  }}
                />
              </div>
            )}

            {/* PM Projects List */}
            {!isRM && myProjects && myProjects.length > 0 && (
              <Card className="mb-8 slide-in bg-white">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle style={{ color: '#1B3240' }}>Active Projects This Month</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">Projects active in the current month (for billing clarity)</p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/projects')}
                    className="border-2"
                    style={{ borderColor: '#62D84E', color: '#62D84E' }}
                  >
                    View All
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {myProjects.slice(0, 5).map((project) => (
                      <div
                        key={project.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className="px-2 py-1 bg-gray-200 text-gray-700 text-xs font-mono rounded">
                              {project.project_id}
                            </span>
                            <span className="font-medium text-gray-900">{project.title}</span>
                            {project.is_activated ? (
                              <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
                                🟢 Active
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">
                                ⚫ Archived
                              </span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span>{project.number_of_consultants} consultants needed</span>
                            <span>•</span>
                            <span>{project.city}, {project.country}</span>
                            <span>•</span>
                            <span>Starts: {new Date(project.start_date).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          {/* Activation Toggle */}
                          <button
                            onClick={() => toggleProjectActivation(project.id, project.is_activated)}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              project.is_activated ? 'bg-green-600' : 'bg-gray-300'
                            }`}
                            title={project.is_activated ? 'Click to deactivate' : 'Click to activate'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                project.is_activated ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate('/projects')}
                            className="text-xs"
                          >
                            View →
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Reservations Overview Panel */}
            <Card className="mb-8 slide-in bg-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle style={{ color: '#1B3240' }} className="flex items-center">
                    <CalendarCheck className="w-5 h-5 mr-2" style={{ color: '#62D84E' }} />
                    Reservations Overview
                  </CardTitle>
                  <p className="text-sm text-gray-600 mt-1">Track your reservation requests and responses</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/reservations')}
                  className="border-2"
                  style={{ borderColor: '#62D84E', color: '#62D84E' }}
                >
                  View All
                </Button>
              </CardHeader>
              <CardContent>
                {/* Stats Row */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  <div className="text-center p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-yellow-600" />
                    <div className="text-2xl font-bold text-yellow-800">
                      {reservationsSummary?.pending_incoming || 0}
                    </div>
                    <div className="text-xs text-yellow-700 font-medium">Pending Incoming</div>
                  </div>
                  <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <Clock className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                    <div className="text-2xl font-bold text-blue-800">
                      {reservationsSummary?.pending_outgoing || 0}
                    </div>
                    <div className="text-xs text-blue-700 font-medium">Pending Outgoing</div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                    <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-600" />
                    <div className="text-2xl font-bold text-green-800">
                      {reservationsSummary?.accepted_total || 0}
                    </div>
                    <div className="text-xs text-green-700 font-medium">Accepted</div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                    <XCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
                    <div className="text-2xl font-bold text-red-800">
                      {reservationsSummary?.rejected_total || 0}
                    </div>
                    <div className="text-xs text-red-700 font-medium">Rejected</div>
                  </div>
                </div>

                {/* Recent Reservations */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3">Recent Reservations</h3>
                  {reservationsSummary?.recent_reservations?.length > 0 ? (
                    <div className="space-y-3">
                      {reservationsSummary.recent_reservations.map((item) => (
                        <div
                          key={item.reservation.id}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => navigate('/reservations')}
                        >
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="font-medium text-sm text-gray-900">
                                {item.resource?.name || 'Resource'}
                              </span>
                              <span className="text-xs text-gray-500">→</span>
                              <span className="text-sm text-gray-600">
                                {item.project?.title || 'Project'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <span className={`px-2 py-0.5 rounded-full ${
                                item.direction === 'incoming' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                              }`}>
                                {item.direction === 'incoming' ? '📥 Incoming' : '📤 Outgoing'}
                              </span>
                              <span>with {item.other_company?.name}</span>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            {getStatusBadge(item.reservation.status)}
                            {item.reservation.conversation_thread_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/messages?thread=${item.reservation.conversation_thread_id}`);
                                }}
                                className="text-xs"
                              >
                                💬
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <CalendarCheck className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                      <p>No reservations yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
          )}
          </>
        )}
      </main>
    </div>
  );
};

export default Dashboard;